import React, { useState, useEffect } from 'react';
import { Spin, message, Steps } from 'antd';
import { IoIosArrowBack } from "react-icons/io";
import { FiMapPin, FiCopy } from "react-icons/fi";
import httpClient from '../../utils/HttpClient';
import { BASE_URL, ORDERS_URL } from '../../constants/Url';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlipayCircleFilled, WechatFilled } from '@ant-design/icons';

const { Step } = Steps;

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (orderId) {
      fetchOrderDetail();
    }
  }, [orderId]);

  const fetchOrderDetail = async () => {
    try {
      setLoading(true);
      const res = await httpClient.get(`${ORDERS_URL}/${orderId}`);
      if (res.data && res.data.status === 200) {
        setOrderData(res.data.data);
      } else {
        message.error(t('text_failed_loading_order_detail'));
      }
    } catch (error) {
      message.error(t('text_failed_loading_order_detail'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success(t('text_copied'));
  };

  const getStepCurrent = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'pending' || s === 'waiting' || s === 'unpaid') return 0;
    if (s === 'paid' || s === 'confirmed' || s === 'processing') return 1;
    if (s === 'shipped') return 2;
    if (s === 'completed') return 3;
    return 0; // Default
  };

  const getStatusText = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'pending' || s === 'waiting' || s === 'unpaid') return t('text_buyer_to_pay');
    if (s === 'paid' || s === 'confirmed' || s === 'processing') return t('text_paid_tobeship');
    if (s === 'shipped' || s === 'completed' || s === 'delivered') return t('text_Shipped');
    if (s === 'cancelled' || s === 'canceled') return t('text_transaction_closed');
    return t('text_unknown_status');
  };

  const getPaymentChannelText = (order) => {
    const method = (order?.paymentId?.method || '').toLowerCase();
    if (method.includes("wechatpay")) return <WechatFilled className="text-[#09b83e] text-[24px]" />;
    if (method.includes('alipay')) return <AlipayCircleFilled className="text-[#1677ff] text-[24px]" />;
    if (order?.paymentId?.method) return order?.paymentId?.method;
    return '-';
  };

  const trackingNumber = orderData?.shippingInfo?.trackingNumber || 
    orderData?.shippingInfo?.trackingNo || 
    orderData?.shippingInfo?.expressNo || 
    orderData?.shippingInfo?.waybillNo || 
    orderData?.trackingNumber || 
    orderData?.trackingNo || 
    orderData?.expressNo;

  const paymentTime = orderData?.paymentTime || 
    orderData?.paidTS || 
    orderData?.paymentTS || 
    orderData?.paidAt || 
    (orderData?.paymentStatus === 'paid' ? orderData?.updatedAt || orderData?.orderedTS : null);

  const currentStep = getStepCurrent(orderData?.status);

  return (
    <div className="w-full min-h-screen flex flex-col font-sans bg-[#f5f5f5] pb-12">
      {/* Desktop Header & Breadcrumb */}
      <div className="hidden lg:flex esi-std-container w-full mx-auto pt-10 pb-6 items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-[#e80d44] transition-colors flex items-center gap-2 font-medium"
        >
          <IoIosArrowBack className="text-xl" />
          <span>{t("text_back")}</span>
        </button>
        <span className="text-gray-300">|</span>
        <h1 className="text-[22px] font-bold text-gray-900 tracking-wide">{t("text_order_detail")}</h1>
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden bg-white pt-4 pb-3 px-4 shadow-sm mb-4">
        <div className="flex items-center justify-center relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-0 text-[24px] text-gray-800"
          >
            <IoIosArrowBack />
          </button>
          <h1 className="text-[17px] font-semibold text-gray-900 tracking-wide">{t("text_order_detail")}</h1>
        </div>
      </div>

      <div className="esi-std-container w-full mx-auto flex flex-col">
        <Spin spinning={loading}>
          {orderData ? (
            <div className="flex flex-col gap-6">
              
              {/* Status Section */}
              <div className="bg-white rounded-xl p-6 lg:p-8 shadow-sm">
                <div>
                  <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">{getStatusText(orderData?.status)}</h2>
                  
                  {orderData?.shippingInfo && (
                    <div className="flex items-start gap-2 text-sm text-gray-700">
                      <FiMapPin className="text-lg mt-0.5 shrink-0" />
                      <div>
                        <span className="font-semibold">{orderData.shippingInfo.recipientName}</span> 
                        <span className="ml-3 font-semibold">{orderData.shippingInfo.phone?.number}</span>
                        <p className="text-gray-500 mt-1">
                          {orderData.shippingInfo.province} {orderData.shippingInfo.city} {orderData.shippingInfo.district} {orderData.shippingInfo.addressLine1} {orderData.shippingInfo.addressLine2}
                        </p>
                      </div>
                    </div>
                  )}

                  {trackingNumber && (
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-gray-700">
                        <span className="font-semibold text-gray-900">{t("text_tracking_number")}:</span>
                        <span className="font-mono text-gray-800 font-medium">{trackingNumber}</span>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(trackingNumber)} 
                        className="text-xs text-[#e80d44] hover:underline flex items-center gap-1 font-medium bg-[#fff0f4] px-2.5 py-1 rounded-md"
                      >
                        <FiCopy /> {t("text_copy")}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Two Columns Layout */}
              <div className="flex flex-col lg:flex-row gap-6 items-start">
                
                {/* Left Column - Product List */}
                <div className="flex-1 w-full bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-100">
                    {/* <span className="text-[#e80d44] font-bold">ESI Store</span> */}
                    <img alt='ESI-Logo' role='button' src={BASE_URL + "/logo.svg"} className='esi-logo' />
                  </div>

                  <div className="flex flex-col gap-5">
                    {orderData?.items?.map((item, idx) => (
                      <div key={idx} className="flex gap-4">
                        <img 
                          src={item.images?.[0]} 
                          alt={item.productName}
                          className="w-[84px] h-[84px] lg:w-[90px] lg:h-[90px] object-cover rounded-lg border border-gray-100"
                        />
                        <div className="flex-1 flex flex-col pt-1">
                          <div className="flex justify-between gap-4">
                            <h3 className="text-sm font-medium text-gray-800 line-clamp-2">
                              {item.productName}
                            </h3>
                            <div className="text-right shrink-0">
                              <div className="text-sm font-semibold text-gray-900">¥{(item.unitPrice || 0).toFixed(2)}</div>
                              <div className="text-xs text-gray-500 mt-1">x{item.qty}</div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-400 mt-2 line-clamp-1">
                            {item.categoryName}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Column - Payment & Order Info */}
                <div className="w-full lg:w-[340px] flex flex-col gap-6 shrink-0">
                  
                  {/* Payment Details */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-4">{t("text_payment_detail")}</h3>
                    <div className="space-y-3 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span>{t("text_product_amount")}</span>
                        <span>¥{(orderData.ororiginalTotal || orderData.total || 0).toFixed(2)}</span>
                      </div>
                      {/* <div className="flex justify-between">
                        <span>运费</span>
                        <span>¥{(orderData.shippingInfo?.shippingFee || 0).toFixed(2)}</span>
                      </div> */}
                      {orderData.couponCode && (
                        <div className="flex justify-between text-[#e80d44]">
                          <span>{t("text_coupon_deduct")} ({orderData.couponCode})</span>
                          <span>-¥{((orderData.ororiginalTotal || 0) - (orderData.total || 0)).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="border-t border-gray-100 pt-3 mt-1 flex justify-between items-center">
                        <span className="font-medium text-gray-900">{t("text_actual_paid")}</span>
                        <span className="text-[#e80d44] text-xl font-bold">¥{(orderData.total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Order Information */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-4">{t("text_order_info")}</h3>
                    <div className="space-y-4 text-xs text-gray-500">
                      <div className="flex items-center justify-between gap-4">
                        <span className="shrink-0 w-16">{t("text_order_num")}</span>
                        <div className="flex items-center gap-2 text-gray-800 flex-1 justify-end">
                          <span className="truncate font-mono">{orderData.orderId}</span>
                          <button onClick={() => copyToClipboard(orderData.orderId)} className="text-gray-400 hover:text-[#e80d44]">
                            <FiCopy />
                          </button>
                        </div>
                      </div>
                      {orderData.orderedTS && (
                        <div className="flex items-center justify-between gap-4">
                          <span className="shrink-0 w-16">{t("text_create_time")}</span>
                          <span className="text-gray-800">{dayjs(orderData.orderedTS).format('YYYY-MM-DD HH:mm:ss')}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-4">
                        <span className="shrink-0 w-16">{t("text_payment_channel")}</span>
                        <span className="text-gray-800 font-medium">{getPaymentChannelText(orderData)}</span>
                      </div>
                      {paymentTime && (
                        <div className="flex items-center justify-between gap-4">
                          <span className="shrink-0 w-16">{t("text_payment_time")}</span>
                          <span className="text-gray-800">{dayjs(paymentTime).format('YYYY-MM-DD HH:mm:ss')}</span>
                        </div>
                      )}
                      {trackingNumber && (
                        <div className="flex items-center justify-between gap-4">
                          <span className="shrink-0 w-16">{t("text_tracking_number")}</span>
                          <div className="flex items-center gap-2 text-gray-800 flex-1 justify-end">
                            <span className="truncate font-mono">{trackingNumber}</span>
                            <button onClick={() => copyToClipboard(trackingNumber)} className="text-gray-400 hover:text-[#e80d44]">
                              <FiCopy />
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-4">
                        <span className="shrink-0 w-16">{t("text_payment_status")}</span>
                        <span className="text-gray-800">{orderData.paymentStatus === 'paid' ? t('text_payment_paid') : t('text_payment_unpaid')}</span>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          ) : (
            <div className="py-20 flex items-center justify-center text-gray-400 bg-white rounded-xl shadow-sm border border-gray-100">
              {t("text_no_order_record")}
            </div>
          )}
        </Spin>
      </div>

      <style jsx global>{`
        /* Finish State (Completed) */
        .site-navigation-steps .ant-steps-item-finish .ant-steps-item-icon {
          background: #fff;
          border-color: #e80d44;
        }
        .site-navigation-steps .ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
          color: #e80d44;
        }
        .site-navigation-steps .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-content > .ant-steps-item-title::after {
          background-color: #e80d44;
        }
        .site-navigation-steps .ant-steps-item-finish .ant-steps-item-title {
          color: #333 !important;
        }

        /* Process State (Current) */
        .site-navigation-steps .ant-steps-item-process .ant-steps-item-icon {
          background: #e80d44;
          border-color: #e80d44;
        }
        .site-navigation-steps .ant-steps-item-process .ant-steps-item-icon > .ant-steps-icon {
          color: #fff;
        }
        .site-navigation-steps .ant-steps-item-process .ant-steps-item-title {
          color: #333 !important;
          font-weight: 500;
        }

        /* Wait State (Future) */
        .site-navigation-steps .ant-steps-item-wait .ant-steps-item-icon {
          background: #e5e5e5;
          border-color: #e5e5e5;
        }
        .site-navigation-steps .ant-steps-item-wait .ant-steps-item-icon > .ant-steps-icon {
          color: #fff;
        }
        .site-navigation-steps .ant-steps-item-wait .ant-steps-item-title {
          color: #999 !important;
        }
      `}</style>
    </div>
  );
}
